(ns hai.ui.components.form.select.searchable-multi-select
  "A server side searchable multi-select field component backed by collection resolvers such as
  `:users/table`. See: `hai.sharing.components/shared-with-filter-field`."
  (:require
   ["react" :as react]
   [hai.ui.components.collection.core :as core]
   [hai.ui.components.collection.pagination :as pagination]
   [hai.ui.components.collection.search :as search]
   [hai.ui.components.collection.settings :as settings]
   [hai.ui.components.collection.table :as table]
   [hai.ui.components.form.core :as form]
   [hai.ui.components.form.input :as input]
   [hai.ui.components.form.select.common :as common]
   [hai.ui.components.form.select.multi-select :as multi-select]
   [hai.ui.rf-sugar :refer [>e >e! defevent-fx]]
   [hai.ui.utils.validate :refer [validate]]
   [humbleai.belt.malli.transform.default-value :as default-value]
   [humbleai.belt.malli.transform.translate-keys :as translate-keys]
   [malli.core :as m]
   [malli.util :as mu]
   [medley.core :refer [assoc-some]]
   [reagent.core :as r]))

(defn style
  [render-attr]
  (comp render-attr :select.render/style))

(def Render
  [:map

   [:select.render/style
    {:optional true
     ::translate-keys/from :style}
    map?]

   [:select.render/label
    {:optional true
     ::translate-keys/from :label}
    ifn?]

   [:select.render/field
    {::translate-keys/from :field
     ::default-value/fn (style :select.render/field)}
    ifn?]

   [:select.render/options
    {::translate-keys/from :options
     ::default-value/fn (style :select.render/options)}
    ifn?]
   [:select.render/option
    {::translate-keys/from :option
     ::default-value/fn (style :select.render/option)}
    ifn?]
   [:select.render/option-label
    {:optional true
     ::translate-keys/from :option-label
     ::default-value/fn :select.render/label}
    ifn?]

   ;; Optional (option -> reason-string | nil). A non-nil reason marks the option
   ;; disabled: it stays visible and hoverable (so the reason can be shown as a
   ;; tooltip) but can't be selected. See `option-radix` / `option-core`.
   [:select.render/disabled-reason
    {:optional true
     ::translate-keys/from :disabled-reason}
    ifn?]

   [:multi-select.render/pills
    {::translate-keys/from :pills
     ::default-value/fn (style :multi-select.render/pills)}
    ifn?]
   [:multi-select.render/pill
    {::translate-keys/from :pill
     ::default-value/fn (style :multi-select.render/pill)}
    ifn?]
   [:multi-select.render/pill-label
    {:optional true
     ::translate-keys/from :pill-label}
    ifn?]])

(def Select
  (reduce
   mu/merge
   form/BaseField
   [input/Input
    table/StaticCollection
    pagination/LimitedCollection
    [:map
     [:select/show-options?
      {:default false}
      :boolean]
     [:select/active-option
      {:optional true}
      :any]
     [:select/render
      {::translate-keys/from :render}
      Render]
     [:select/transform-selected
      {:optional true
       ::translate-keys/from :transform-selected}
      ifn?]
     [:select/transform-options
      {:optional true
       ::translate-keys/from :transform-options}
      ifn?]]]))

(def Settings
  [:map
   ;; NOTE searchable selects do not currently support swappable views
   [:collection/view
    {:default nil}
    :nil]])

(def decode-select-props (m/decoder Select table/transformer))

;; TODO not required for radix
(defn field-container
  [{{:select.render/keys [field]} :select/render :as select}]
  (let [ref (react/useRef)
        select (assoc select :select/input-ref (react/useRef))]
    (common/on-click-out ref (fn [] (>e [:select/hide-options select])))
    [:div.hai_ui_components_form_select_components__field-container
     {:ref ref
      :style {:flex-grow "1"}}
     [field select]]))

(defevent-fx :multi-select.searchable/search
  [{:keys [db]} ?select direction]
  (let [form (multi-select/->form db ?select)]
    (if (-> form :form/current-value :table/search)
      ;; NOTE Deliberately do NOT clear `:table/rows` here. The resolve is
      ;; debounced 500ms, and the popover is only open while rows are non-empty
      ;; (see `options-radix`) -- clearing on every keystroke collapsed and
      ;; reopened the dropdown between keys. Keeping the previous results visible
      ;; until the new ones arrive (they're swapped in by `:on-success`) is the
      ;; usual typeahead behaviour and removes the flicker.
      {:dispatch [:table/debounce-resolve ?select
                  {:on-success (fn [{:table/keys [rows]}]
                                 ;; TODO this should be replace by `:select/set-active-option-to` with
                                 ;; `:first`. There are issues with event ordering of form setup and
                                 ;; this one, form setup happens too late.
                                 (>e [:select/set-active-option ?select (first rows)])
                                 (>e [:select/show-options ?select]))}]}
      {:dispatch [:table/debounce-stop ?select]})))

(defn select-form
  [{:as select
    :table/keys [id]
    :select/keys [transform-selected render]
    :form.field/keys [placeholder set-value!]
    :form.field.input/keys [on-key-down]}]
  (r/with-let [form-render
               (fn [form]
                 [:f> field-container
                  (assoc-some form
                              ;; TODO figure out why for tables `form-render` takes
                              ;; the table but here it takes the form. It's probably
                              ;; due to this component being a field and not a table,
                              ;; not sure? Regardless settings currently looks for
                              ;; the form on the `:table/form` key.
                              :select/render render
                              ;; TODO this should be added to the field directly
                              :form.field.input/on-key-down on-key-down
                              :table/form form)])

               on-search
               (fn [_] (>e [:multi-select.searchable/search select]))

               form-schema
               (-> [:map
                    {:form/render form-render
                     :form/initial-value {:table/rows []
                                          :multi-select/selected []}}
                    ;; Select items are not editable
                    [:table/rows [:vector :any]]
                    ;; TODO should this be `:select/selected`? Both single and multi selected have a
                    ;; notion of selected but their values differ (vector of x, vs. x)?
                    [:multi-select/selected
                     { ;; TODO can't use :default?
                      ;; :default #{}
                      :form.field/on-change
                      (fn [selected]
                        (if (seq selected)
                          (cond-> selected
                            transform-selected transform-selected
                            :always (set-value!))
                          (set-value! nil)))}
                     [:vector :any]]]
                   (search/process-schema select (cond-> {:form.field/on-change on-search}
                                                   placeholder (assoc :form.field/placeholder placeholder)))
                   (settings/process-schema select Settings))

               form
               {:form/id id
                :form/schema form-schema}]
    [form/component form]))

(defn field
  [select]
  (let [select (-> select
                   (assoc :collection/app-db-schema Select)
                   (decode-select-props)
                   ;; TODO Add 'more' indicator to dropdown?
                   (assoc :table/pagination {:table/per-page 5})
                   (assoc :table/id [(:form.field/form-id select)
                                     (:form.field/attr select)]))]
    (validate Select select)
    (>e! [::core/setup select])
    (fn [_]
      [select-form select])))
